import Laboratory from './Laboratory';
import Patient from './Patient';
import Slot from './Slot';
import capitalize from '../utils/capitalize';
import AppointmentRepository from '../repositories/AppointmentRepository';

export interface IAppointment {
  id?: number;
  laboratory?: Laboratory;
  files?: Array<string>;
  start?: Date;
  end?: Date;
  slot?: Slot;
  patient?: Patient;
  status?: 'pending' | 'confirmed' | 'canceled' | 'completed';
  reference?: string;
  reason?: string;
  results?: string;
  checked_at?: Date;
}

export default class Appointment implements IAppointment {
  id?: number;
  laboratory?: Laboratory;
  files?: string[] | undefined;
  documents?: { id: string; name: string }[];
  start?: Date;
  end?: Date;
  patient?: Patient;
  slot?: Slot;
  status?: 'pending' | 'confirmed';
  reference?: string;
  reason?: string;
  results?: string;
  partnershipId?: string;
  checked_at?: Date;

  constructor(obj: Partial<IAppointment>) {
    Object.assign(this, obj);
  }

  update(obj: Partial<IAppointment>) {
    Object.assign(this, obj);
  }

  static createFromEntity(obj: Partial<any>) {
    return new Appointment({
      id: obj.id,
      patient: new Patient(obj.patient),
      laboratory: new Laboratory(obj.laboratory),
      files: [],
      start: new Date(obj.start),
      end: new Date(obj.end),
      slot: new Slot(obj.slot),
      status: obj.status || 'completed',
      reference: obj.reference,
      reason: obj.reason,
      results: obj.results,
      checked_at: obj.checked_at ? new Date(obj.checked_at) : undefined
    });
  }

  toEntity() {
    type Entity = {
      id?: string | Number;
      patient_id?: string | Number;
      slot_id?: string | Number;
      laboratory_id?: string | Number;
      reason?: string;
      results: string;
      documents?: string[];
      partnership_id?: string;
    };
    let entity: Entity = {
      id: this.id,
      patient_id: this.patient?.id,
      slot_id: this.slot ? this.slot.id : 0,
      laboratory_id: this.laboratory ? this.laboratory.id : undefined,
      reason: this.reason || '',
      results: this.results || 'website',
      partnership_id:
        this.partnershipId !== 'default' ? this.partnershipId : undefined
    };

    if (this.documents) {
      entity.documents = this.documents.map((document) => document.id);
    }

    return entity;
  }

  getStartDate() {
    return this.start || new Date();
  }

  getEndDate() {
    return this.end || new Date();
  }

  delete() {
    return AppointmentRepository.delete(this);
  }

  getDayString() {
    let start = this.getStartDate();

    if (this.slot?.id) {
      start = this.slot.start;
    }

    return capitalize(
      start.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    );
  }

  get isToday() {
    const today = new Date();
    return (
      this.start?.getDate() === today.getDate() &&
      this.start?.getMonth() === today.getMonth() &&
      this.start?.getFullYear() === today.getFullYear()
    );
  }

  getHourString() {
    let start = this.getStartDate();
    let end = this.getEndDate();

    if (this.slot?.id) {
      start = this.slot.start;
      end = this.slot.end;
    }

    let hourString = `${String(start.getHours()).padStart(2, '0')}h${String(
      start.getMinutes()
    ).padStart(2, '0')}`;

    if (start.getTime() !== end.getTime()) {
      hourString += ` - ${String(end.getHours()).padStart(2, '0')}h${String(
        end.getMinutes()
      ).padStart(2, '0')}`;
    }

    return hourString;
  }

  getStatusMeta() {
    const status = {
      pending: {
        label: 'En attente',
        className: 'warning'
      },
      confirmed: {
        label: 'Confirmé',
        className: 'success'
      },
      canceled: {
        label: 'Annulé',
        className: 'error'
      },
      completed: {
        label: 'Terminé',
        className: 'medium'
      }
    };

    if (this.status) {
      return status[this.status];
    }
  }

  isCheckinAllowed() {
    return Date.now() >= this.getStartDate().getTime() - 3600000;
  }

  isFinished() {
    return Date.now() >= this.getStartDate().getTime(); // 12 hours after end of appointment, the appointment is considered finished
  }
}
